'use client';

import dynamic from 'next/dynamic';
import clsx from 'clsx';
import type { DenormalizedCreationState } from '@pixum/types';
import styles from './CreationThumbnail.module.css';

// @pixum/creation-preview uses `document` at module evaluation — must skip SSR
const Editor = dynamic(
  () => import('@pixum/creation-preview').then(m => ({ default: m.Editor })),
  { ssr: false },
);

export interface CreationThumbnailProps {
  /** URL of the fallback image, shown when `creation` is absent or null. */
  previewUrl: string;
  /** Alt text for the fallback image. */
  alt: string;
  /**
   * Denormalized creation state from the Pixum data layer.
   * When provided, renders the interactive `Editor` component (client-only,
   * loaded via `next/dynamic` with `ssr: false`).
   * When absent or null, renders a plain `<img>` using `previewUrl`.
   */
  creation?: DenormalizedCreationState | null;
  /** Intrinsic width for the fallback `<img>`. Has no effect on the Editor path. */
  width?: number;
  /** Intrinsic height for the fallback `<img>`. Has no effect on the Editor path. */
  height?: number;
  /**
   * Additional CSS class merged with the default image styles.
   * Only applies to the fallback `<img>` path — the Editor manages its own styles.
   * Use this to override `object-fit`, sizing, or layout from the call site.
   */
  className?: string;
}

/**
 * Renders a product creation thumbnail — either as an interactive `Editor`
 * (when denormalized creation data is available) or as a plain image fallback.
 *
 * This is a content-only component with no container or border — the caller
 * is responsible for wrapping it in a container that provides sizing,
 * border-radius, overflow clipping, and any other frame styles.
 *
 * @example Card thumbnail (with container provided by ArticlePreviewThumbnail)
 * ```tsx
 * <div className={styles.imageContainer}>
 *   <CreationThumbnail creation={creation} previewUrl={url} alt="My book" />
 * </div>
 * ```
 *
 * @example Dialog preview (container in dialog CSS, no border)
 * ```tsx
 * <div className={styles.preview}>
 *   <CreationThumbnail creation={creation} previewUrl={url} alt="My book" width={240} height={240} />
 * </div>
 * ```
 */
export function CreationThumbnail({
  previewUrl,
  alt,
  creation,
  width = 164,
  height = 164,
  className,
}: CreationThumbnailProps) {
  if (creation) {
    return <Editor denormalizedCreation={creation} pagegroupIndex={0} withAnimation={false} />;
  }

  return (
    <img
      src={previewUrl}
      alt={alt}
      width={width}
      height={height}
      className={clsx(styles['image'], className)}
    />
  );
}
